import _ from 'lodash'
import audioPlayback from 'src/services/audio/AudioPlaybackService'
import { RING_ANNOUNCEMENT_MS, RING_TITLE_CUE, RING_TITLE_CUE_INTERVAL_MS } from 'src/constants/incoming-call-ring'
import { TRANSFER_TASK_TYPES } from 'src/constants/taskrouter-task-types'
import { mapActions, mapGetters, mapState } from 'vuex'
import { sendToMain } from 'src/plugins/helpers/electron'
import { fetchFullCallerName, fetchFullCallerNumber } from 'src/plugins/helpers/full-phone-numbers'
import { receivesFullPhoneNumbers } from 'src/plugins/helpers/full-phone-numbers-channel'
import { containsMaskedPhoneNumber, isMaskedPhoneNumber } from 'src/plugins/helpers/phone-masking'

export default {
  data () {
    return {
      titleCueInterval: null,
      titleCueOriginalTitle: null,
      notificationMixinTornDown: false,
      // The communication whose reservation notification is currently on screen.
      // Dismissal reads this rather than the caller's reservation state, so it stays
      // correct however that state has moved on.
      announcedReservationCommunicationId: null
    }
  },

  computed: {
    ...mapState([
      'enableAudio',
      'notifications',
      'ringGroups',
      'dialer',
      'communicationNotifiedDesktop',
      'incomingTwilioRing',
      'voicemailNotifiedDesktop',
      'contactNotifiedDesktop',
      'ringtoneAudio',
      'appointmentNotifiedDesktop',
      'reminderNotifiedDesktop',
      'notificationAudio'
    ]),
    ...mapState('cache', ['currentCompany']),
    ...mapState(['isWidget', 'isSalesforceWidget', 'isHubSpotWidget']),
    ...mapGetters(['isSoftphoneWidget']),

    // Only the new engine plays call audio through our own audio elements.
    usesTaskRouterAudio () {
      return !!this.currentCompany?.use_new_engine
    }
  },

  methods: {
    ...mapActions([
      'setNotifications',
      'addCommunicationNotifiedDesktop',
      'addVoicemailNotifiedDesktop',
      'addContactNotifiedDesktop',
      'addAppointmentNotifiedDesktop',
      'addReminderNotifiedDesktop',
      'removeCommunicationNotifiedDesktop',
      'removeVoicemailNotifiedDesktop',
      'removeContactNotifiedDesktop',
      'removeAppointmentNotifiedDesktop',
      'removeReminderNotifiedDesktop',
      'setShowIncomingCallNotification'
    ]),

    // The audio elements are created by MainLayout, which the embedded softphone
    // surfaces (Salesforce, HubSpot widgets) never mount — they reach the dialer
    // through `webrtc.vue`. There is nothing to play there, and that is not the
    // browser refusing anything, so callers must not read silence on those
    // surfaces as blocked playback. Takes the same flag as `playAudio` so it
    // always reports on the element that would actually be played.
    hasAudioElement (shouldPlayRingtone = false) {
      return shouldPlayRingtone
        ? !!this.ringtoneAudio
        : !!this.notificationAudio
    },

    // On the new engine the inbound ringtone is an HTMLAudioElement we play
    // ourselves (the Worker SDK only hands us a reservation), so `enableAudio`
    // — which only turns true after the page has been clicked/typed in — made a
    // freshly loaded tab ring silently. There, always attempt playback and let
    // the browser decide, so a microphone grant or an earlier gesture can carry
    // the ring; resolve to whether it was actually audible so callers can fall
    // back. The old engine gets its ring from the Twilio Voice SDK and keeps the
    // original gate untouched.
    playAudio (shouldPlayRingtone = false, source = null) {
      const audio = shouldPlayRingtone
        ? this.ringtoneAudio
        : this.notificationAudio

      if (!this.usesTaskRouterAudio) {
        if (!this.enableAudio || !audio) {
          return Promise.resolve(false)
        }

        const promise = audio.play()

        if (promise === undefined) {
          return Promise.resolve(true)
        }

        return promise
          .then(() => true)
          .catch(err => {
            // Auto-play was prevented
            // Show a UI element to let the user manually start playback
            console.log(err)
            return false
          })
      }

      return audioPlayback.play(audio).then(played => {
        // One line per sound: tells whether the browser accepted playback, and
        // whether the tab was hidden, the element ever unlocked, and an element
        // existed at all — enough to tell "no element on this surface" and "never
        // unlocked" apart from "unlocked but this play failed".
        console.log('playAudio: playback attempt', {
          source: source || (shouldPlayRingtone ? 'ring' : 'notification'),
          played: played,
          hasElement: !!audio,
          // Named rather than assumed: a refusal (NotAllowedError) and an
          // unplayable source (NotSupportedError — a 404 or unreachable API) are
          // both silence, with entirely different causes.
          error: played ? null : audioPlayback.lastErrorName,
          unlocked: audioPlayback.unlocked,
          visibility: document.visibilityState,
          paused: audio?.paused,
          volume: audio?.volume
        })

        return played
      })
    },

    // Stops the looping ring only. `notificationAudio` is a one-shot ding with
    // nothing to stop, so a caller that ever loops it would need to extend this.
    stopAudio () {
      // The new engine can start the ring while `enableAudio` is still false, so
      // the stop path must not be gated on it — otherwise a looping ringtone
      // would outlive the call.
      if (!this.usesTaskRouterAudio && !this.enableAudio) {
        return
      }

      if (!this.ringtoneAudio) {
        return
      }

      this.ringtoneAudio.pause()
    },

    // Last-resort notification for an inbound call whose ringtone the browser
    // refused to play. Notifications are not subject to the autoplay policy, so
    // an agent in another tab still gets told about the call.
    notifyBlockedRingtone ({ title, body, tag }) {
      if (!window.Push || !window.Push.Permission.has()) {
        // Notifications are the population most likely to need this fallback —
        // a reloaded, never-touched tab — so losing them too must be loud in the
        // log, and the caller is expected to fall back again.
        console.error('notifyBlockedRingtone: ringtone was blocked and notification permission is not granted')
        return Promise.resolve(null)
      }

      return window.Push.create(title, {
        body: body,
        icon: 'notification-icons/call.png',
        tag: tag,
        // Matches how call notifications are configured elsewhere: bounded by the
        // same window as the ringtone rather than sticky, since it is closed
        // explicitly once the call stops ringing.
        requireInteraction: false,
        timeout: RING_ANNOUNCEMENT_MS,
        // Focus only: the notification is closed through the handle the caller
        // keeps, so this does not depend on how push.js binds `this`.
        onClick: () => {
          window.focus()
        }
      }).catch(err => {
        console.log('notifyBlockedRingtone failed', err)
        return null
      })
    },

    // Absolute last resort: no ringtone and no notification permission. Flashing
    // the tab title is the only cue left that an unfocused tab can still show.
    startTitleCue () {
      // Belt for a late async caller: once teardown has run no hook is left to
      // clear the interval, so the title would flash for the rest of the session.
      if (this.titleCueInterval || this.notificationMixinTornDown) {
        return
      }

      this.titleCueOriginalTitle = document.title
      let showingCue = false

      this.titleCueInterval = setInterval(() => {
        showingCue = !showingCue

        if (showingCue) {
          document.title = RING_TITLE_CUE
          return
        }

        this.restoreCuedTitle()
      }, RING_TITLE_CUE_INTERVAL_MS)
    },

    // Only ever overwrite a title we wrote ourselves. The router rewrites
    // document.title on navigation, and a route change while the cue text was
    // showing would otherwise be reverted on the next tick and then latched.
    restoreCuedTitle () {
      if (document.title !== RING_TITLE_CUE) {
        this.titleCueOriginalTitle = document.title
        return
      }

      document.title = this.titleCueOriginalTitle
    },

    stopTitleCue () {
      if (!this.titleCueInterval) {
        return
      }

      clearInterval(this.titleCueInterval)
      this.titleCueInterval = null

      // Compared against null, not truthiness: a page whose title was empty when
      // the cue started would otherwise keep the cue text permanently.
      if (this.titleCueOriginalTitle !== null) {
        this.restoreCuedTitle()
        this.titleCueOriginalTitle = null
      }
    },

    processRemoveFromNotification (communication) {
      if (!communication) {
        return
      }

      this.closeCallNotifications('incomingCall', communication.id)
    },

    closeCallNotifications (type = 'incomingCall', communicationId = null, forceClose = false) {
      if (!communicationId ||
        type !== 'incomingCall') {
        return
      }

      // Twilio ring is the single source of truth for the incoming-call pop-up:
      // while Twilio is actively ringing this agent for this communication, no
      // out-of-order status update may close it. A user-initiated reject passes
      // forceClose and still closes immediately.
      if (!forceClose &&
        this.incomingTwilioRing === communicationId) {
        return
      }

      const notificationCommId = _.get(this.notifications, 'incomingCall.communication.id', null)

      if (notificationCommId === communicationId || forceClose) {
        this.$closeActionNotification(type)
        this.closeDesktopNotification(communicationId, 'communication')
      }
    },

    closeDesktopNotification (communicationId, type) {
      const notification = this.communicationNotifiedDesktop.find(notification => notification.id === communicationId)

      if (notification) {
        notification.close()
        this[`remove${this.$options.filters.capitalize(type)}NotifiedDesktop`](communicationId)
      }
    },

    // Announces an incoming TaskRouter reservation on the OS: desktop push
    // notification, paired with announceIncomingCallOnDesktop for the window coming
    // to the front and the Windows taskbar flash.
    //
    // Built entirely from the reservation's own task attributes rather than the
    // `.user.desktop.communication.new_call` broadcast that announces legacy-engine
    // calls. The broadcast is a separate delivery path from the reservation, so it
    // can arrive late, arrive for a reservation this client never got, or not
    // arrive at all — announcing off the reservation makes the notification a
    // guarantee that this agent was actually invited, with no ordering to race.
    //
    // Deliberately skips checkCommunicationMatchesUserAccessibility: the
    // reservation IS the authorization. TaskRouter already selected this agent for
    // this task, so a line- or user-access filter here could only suppress a call
    // the agent is being rung for right now.
    //
    // callerNumber and callerName are the caller's number and name read back in full, for an Admin or Supervisor of
    // an account that hides numbers: the task carries them masked for every worker it rings.
    handleReservationDesktopNotification (task, callerNumber = null, callerName = null) {
      // Belt to the reachability braces: only the TaskRouter reservation handler
      // calls this, so the flag can never be false here. Asserting it anyway keeps
      // "this cannot touch the legacy engine" true by reading one line, rather than
      // by tracing every caller.
      if (!this.currentCompany?.use_new_engine) {
        return
      }

      const attributes = task && task.attributes

      if (!attributes) {
        console.error('handleReservationDesktopNotification: reservation carried no task attributes')
        return
      }

      // Logged, not silent. This suppression is the very failure mode the feature
      // exists to remove — an agent who never sees the call — so if it happens in
      // production there has to be something in the console saying why. Matches
      // notifyBlockedRingtone, which reports the same denied-permission condition.
      if (!window.Push || !window.Push.Permission.has()) {
        console.error('handleReservationDesktopNotification: no desktop notification for the incoming call — OS notification permission is not granted', {
          hasPush: !!window.Push,
          communicationId: attributes.communication_id
        })
        return
      }

      // Honors the same per-user opt-out the legacy engine applies server-side in
      // SendCallDesktopNotificationJob before broadcasting.
      if (this.profile && this.profile.calls_desktop_notifs === false) {
        return
      }

      const communicationId = parseInt(attributes.communication_id, 10)

      if (!communicationId) {
        return
      }

      // Same store the broadcast-driven notifications register in, so one
      // announcement per communication no matter which path created it, and so
      // closeDesktopNotification tears this one down unchanged.
      if (this.communicationNotifiedDesktop.find(item => item.id === communicationId)) {
        return
      }

      const contactInfo = attributes.contact_info || null

      const masksNumber = isMaskedPhoneNumber(contactInfo?.phone)
      const masksName = containsMaskedPhoneNumber(contactInfo?.name)

      // The slot is claimed while the caller loads, so a reservation that ends meanwhile announces nothing
      if (!callerNumber && !callerName && (masksNumber || masksName) && receivesFullPhoneNumbers(this.currentCompany, this.profile?.user_roles)) {
        this.announcedReservationCommunicationId = communicationId

        return Promise.all([
          masksNumber ? fetchFullCallerNumber(communicationId, contactInfo.phone) : null,
          masksName ? fetchFullCallerName(communicationId, contactInfo.name) : contactInfo.name
        ]).then(([number, name]) => {
          if (this.announcedReservationCommunicationId === communicationId) {
            return this.handleReservationDesktopNotification(task, number, name)
          }
        })
      }

      const isTransfer = TRANSFER_TASK_TYPES.includes(attributes.task_type)
      const lineSuffix = attributes.line_name ? ` on ${attributes.line_name} line.` : '.'

      // contact_info is not guaranteed — transfer tasks in particular may arrive
      // without it. The no-contact fallback branches on the task type for the same
      // reason the title does: keying it on contact_info alone would put a
      // "Transferred call" body under an "Incoming Call" title for a plain inbound
      // task that happened to have no contact_info.
      //
      // The name is not HTML-escaped: a Notification body is rendered as plain text
      // by the OS, so escaping would only turn O'Brien into O&#39;Brien on screen.
      const subject = contactInfo
        ? `From: ${this.$options.filters.fixName(callerName || contactInfo.name)} ${this.$options.filters.displayPhone(callerNumber || contactInfo.phone)}`
        : (isTransfer ? 'Transferred call' : 'Incoming call')
      const body = `${subject}${lineSuffix}`

      const self = this

      // Recorded before the notification exists, not in the .then below. When the
      // desktop Notification constructor throws, push.js falls back to a
      // ServiceWorker notification and resolves several macrotasks later — long
      // enough for the reservation to be canceled first. Claiming the slot up front
      // means such a dismissal is seen by the .then rather than being a no-op that
      // leaves a notification nothing will ever close.
      this.announcedReservationCommunicationId = communicationId

      // Returns the chain so the outcome is awaitable. Callers in the ring path
      // deliberately do not await it — announcing must not delay anything — but a
      // test cannot otherwise observe the failure branch, which is the branch that
      // matters most here.
      return window.Push.create(isTransfer ? 'Transferred Call' : 'Incoming Call', {
        icon: 'notification-icons/call.png',
        body: body,
        // Matches the tag the broadcast-driven notification would use, so the two
        // replace rather than stack if both ever fire for one communication.
        tag: 'communication-notification-' + communicationId,
        requireInteraction: false,
        timeout: 60000,
        // Bringing the app forward is the whole point of the click — the reservation
        // modal the agent needs to act on is already mounted, so deliberately no
        // route change that would navigate away from it.
        onClick: () => {
          window.focus()
          self.closeDesktopNotification(communicationId, 'communication')
          self.announceIncomingCallOnDesktop()
        },
        // console.error, not console.log: the agent got no visible announcement for a
        // ringing call, which is a failure worth finding in a session replay rather
        // than something to bury among ordinary logs.
        onError: (err) => {
          self.removeCommunicationNotifiedDesktop(communicationId)
          self.reportFailedReservationNotification('notification error', err, communicationId)
        },
        onClose: () => {
          self.removeCommunicationNotifiedDesktop(communicationId)
        }
      }).then((data) => {
        // The reservation ended while the OS was still creating this. Close it now
        // rather than register it — nothing would come along to dismiss it, and the
        // agent would be looking at an incoming call that is already over.
        if (this.announcedReservationCommunicationId !== communicationId) {
          data.close()
          return
        }

        this.addCommunicationNotifiedDesktop({
          id: communicationId,
          close: data.close
        })
      }).catch((err) => {
        // Reached when the OS refuses the notification after the permission check
        // passed — Push.Permission.has() can be stale against live OS state, so this
        // is the likelier production failure, not the guard above.
        //
        // Release the slot claimed above: no notification exists, so a later dismissal
        // has nothing to close and should not think otherwise.
        if (this.announcedReservationCommunicationId === communicationId) {
          this.announcedReservationCommunicationId = null
        }

        this.reportFailedReservationNotification('could not create the notification', err, communicationId)
      })
    },

    // Sentry has no CaptureConsole integration, so a console line alone only surfaces
    // in a LogRocket replay someone thought to open — nobody is paged. An agent who
    // got no visible ring for a live call warrants an alert, so the genuine failures
    // are captured as exceptions, the way the dialer reports a failed answer.
    //
    // Only unexpected failures come here. A denied OS notification permission is a
    // user setting, not a fault, and is logged without capture so it cannot bury real
    // alerts in noise.
    reportFailedReservationNotification (what, err, communicationId) {
      console.error(`handleReservationDesktopNotification: ${what}`, err)

      if (this.$Sentry) {
        this.$Sentry.captureException(err, { extra: { communicationId: communicationId } })
      }
    },

    // Dismisses the notification raised for a reservation, off the reservation
    // lifecycle rather than the company-wide communication-update broadcast that
    // closes legacy-engine notifications. That broadcast may also close it and both
    // are idempotent, but only the reservation ending is guaranteed to reach the
    // client that raised the notification.
    //
    // Takes no argument on purpose. Keying the dismissal off the caller's current
    // reservation or task would tie it to state the caller is usually in the middle
    // of clearing; remembering what was announced means every ending — canceled,
    // timed out, rejected, accepted, or the dialer being destroyed mid-ring — closes
    // the right notification without the caller having to still hold the task.
    dismissReservationDesktopNotification () {
      if (!this.announcedReservationCommunicationId) {
        return
      }

      this.closeDesktopNotification(this.announcedReservationCommunicationId, 'communication')
      this.announcedReservationCommunicationId = null
    },

    // Brings the desktop window out of minimized to the front, and flashes the
    // Windows taskbar button when the OS denies it foreground.
    //
    // Distinct from MainLayout's restoreApp (which sends restore_app) so this
    // stronger behavior stays confined to the reservation path — restore_app is
    // shared with the legacy engine and must keep behaving exactly as it does.
    announceIncomingCallOnDesktop () {
      if (!this.currentCompany?.use_new_engine) {
        return
      }

      sendToMain('announce_incoming_call')
    },

    processActionNotification (communication, type) {
      if (this.isWidget && !this.isSoftphoneWidget) {
        return
      }
      const name = { data: '' }
      const companyName = { data: '' }
      const firstAttachment = { data: null }
      const contactId = { data: null }
      const communicationId = { data: null }
      const campaignId = { data: _.get(communication, 'campaign_id', null) }
      const message = { data: '' }

      if (!communication.contact) {
        return
      }

      if (type !== 'mention') {
        name.data = communication.contact.name ? communication.contact.name : this.$options.filters.displayPhone(communication.contact.phone_number)
        companyName.data = communication.contact.company_name
        firstAttachment.data = _.get(communication.attachments, '0.url', null)
      }

      const params = { data: {} }
      switch (type) {
        case 'sms':
          params.data = {
            title: name.data,
            message: communication.body,
            attachment: firstAttachment.data,
            type: 'sms',
            contactId: communication.contact.id,
            communicationId: communication.id,
            campaignId: campaignId.data,
            ringGroupId: communication.ring_group_id
          }
          break
        case 'missed voicemail':
          params.data = {
            title: name.data,
            message: 'Missed Call with Voicemail',
            messageIcon: 'call-voicemail-icon',
            type: 'call',
            contactId: communication.contact.id,
            communicationId: communication.id,
            campaignId: campaignId.data,
            ringGroupId: communication.ring_group_id
          }
          break
        case 'mention':
          name.data = _.get(communication, 'mentioner_user_name', '')
          contactId.data = _.get(communication, 'contact_id', null)
          communicationId.data = _.get(communication, 'mention_subject_id', null)
          message.data = _.get(communication, 'preview_text', '')
          params.data = {
            title: name.data,
            message: message.data,
            type: 'mention',
            contactId: contactId.data,
            communicationId: communicationId.data,
            ringGroupId: communication.ring_group_id
          }
          break
        case 'missed call':
          params.data = {
            title: name.data,
            message: 'Missed Call',
            type: 'call',
            contactId: communication.contact.id,
            communicationId: communication.id,
            ringGroupId: communication.ring_group_id
          }
          break
        case 'abandoned call':
          params.data = {
            title: name.data,
            message: 'Abandoned Call',
            type: 'call',
            contactId: communication.contact.id,
            communicationId: communication.id,
            ringGroupId: communication.ring_group_id
          }
          break
        case 'call':
          const campaignName = _.get(communication, 'campaign.name', null)
          const ringGroupId = communication.ring_group_id
          const ringGroupName = _.get(communication, 'ring_group.name', null)
          const phoneNumber = _.get(communication, 'contact.phone_number', null)

          params.data = {
            title: name.data,
            message: companyName.data,
            contactId: communication.contact.id,
            communicationId: communication.id,
            campaignId: campaignId.data,
            campaignName: campaignName,
            ringGroupId: ringGroupId,
            ringGroupName: ringGroupName,
            phoneNumber: phoneNumber,
            communication: communication,
            contact: communication.contact,
            type: 'incomingCall'
          }
          break
      }

      if (!_.isEmpty(params.data)) {
        if (params.data.type === 'incomingCall') {
          console.log('processActionNotification - params.data', params.data)
          this.setShowIncomingCallNotification(true)
        }

        this.$actionNotification(params.data)
      }
    }
  },

  beforeDestroy () {
    this.notificationMixinTornDown = true
    this.stopTitleCue()
  }
}
